import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select, message } from "antd";
import ReportJewelryReportService from "../../../services/reports-Jewelry-service";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { VENDOR_CLICKS_CONFIG } from "./_shared/jewelryReportConfigs";
import JewelryDetailSearchTable from "./_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "./_shared/jewelryHelpers";

const { Option } = Select;

// This report groups clicks BY vendor. A Vendor account narrows by Retailer(s);
// a Retailer account (who carries many vendors) narrows by Vendor(s) instead.
const JewelryVendorClickReportMain = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMembershipType, setDealerMembershipType] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [retailerIds, setRetailerIds] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [vendorIds, setVendorIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [vendorClicksData, setVendorClicksData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleCheckDealermembers(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckDealermembers = (id) => {
    ReportJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const memberType = loginDetials.loginDetials.responseData.dealerMemberTypeName;
        setDealerMembershipType(memberType);
        const fd = memberType === "Vendor" || memberType === "VendorDiamond" || memberType === "VendorGemstone" ? 1 : memberType === "Retailer" || memberType === "Combined" ? 2 : 3;
        setForDealer(fd);
        handleLoadVendorClicksData(id, fd, dataRangeFrom, dataRangeTo, [], []);
        // Retailer(s) filter only makes sense for a Vendor account (they have
        // many retailers to narrow down); sp_Admin_RetailerDetails resolves
        // its list via "retailers connected to this VendorID's JewelryCollection",
        // which is always empty for a non-Vendor caller -- skip the call and
        // hide the filter for them instead (Bug 1765).
        if (memberType === "Vendor" || memberType === "VendorDiamond" || memberType === "VendorGemstone") {
          handleGetRetailerDropdownData();
        }
        // Vendor(s) filter mirrors the above for a Retailer account -- they
        // carry many vendors and want to narrow this vendor-wise breakdown.
        if (memberType === "Retailer" || memberType === "Combined") {
          handleGetVendorDropdownData(id);
        }
      }
    });
  };

  const handleGetRetailerDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleGetVendorDropdownData = (id) => {
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
  };

  const handleLoadVendorClicksData = (dealerIdArg, forDealerArg, fromDate, toDate, rIds, vIds) => {
    setLoading(true);
    ReportJewelryReportService.GetClicksBasedOnTopVendor({
      dealerID: dealerIdArg.toString(),
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
      reporttype: "Dealer",
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData?.Table) {
          const rows = response.data.responseData.Table;
          setVendorClicksData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.Total || 0), 0));
        } else {
          setVendorClicksData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setVendorClicksData([]);
        setTotalClicks(0);
      });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      ReportJewelryReportService.GetClicksBasedOnTopVendor,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg.toString(),
        forDealer: forDealerArg ? forDealerArg.toString() : "",
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: extractDealerIds(rIds).toString(),
        vendorIds: extractDealerIds(vIds).toString(),
        reporttype: "Dealer",
      }),
      fromDate,
      toDate,
      "Total"
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterValue("");
    handleLoadVendorClicksData(dealerId, forDealer, dataRangeFrom, dataRangeTo, retailerIds, vendorIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = (vendorName) => {
    setFilterValue(vendorName);
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(VENDOR_CLICKS_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [`Retailers: ${retailerNames}`, `Total Clicks: ${totalClicks}`],
        fileName: "Jewelry-Vendor-Clicks-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId ? dealerId.toString() : "",
      forDealer: forDealer ? forDealer.toString() : "",
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(retailerIds).toString(),
      vendorIds: extractDealerIds(vendorIds).toString(),
      reporttype: "Dealer",
      reportName: "VendorClick",
      barViewDimensions: chartType === "bar" ? "VendorClick" : "",
    };
    setActivityPdfLoading(true);
    ReportJewelryReportService.ExportMetricPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const filename = `Jewelry_VendorClick_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, filename, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  const sortedVendors = [...vendorClicksData].sort((a, b) => Number(b.Total || 0) - Number(a.Total || 0));
  const topVendor = sortedVendors[0];
  const top3Sum = sortedVendors.slice(0, 3).reduce((sum, row) => sum + Number(row.Total || 0), 0);
  const top3Pct = totalClicks > 0 ? Math.round((top3Sum / totalClicks) * 100) : 0;
  const topPct = totalClicks > 0 && topVendor ? ((Number(topVendor.Total) / totalClicks) * 100).toFixed(2) : 0;

  return (
    <ReportScaffold
      config={VENDOR_CLICKS_CONFIG}
      report={{
        [VENDOR_CLICKS_CONFIG.dataKey]: vendorClicksData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onDownloadActivityPdf={handleDownloadActivityPdf}
      downloadingActivityPdf={activityPdfLoading}
      onChartClick={handleSliceClick}
      onChartTypeChange={setChartType}
      insight={
        topVendor && (
          <div className="edr-insight">
            💡 <b>{topVendor.DealerCompany}</b> {t("is the standout vendor at")} <b>{topPct}%</b> {t("of clicks")}, {t("and the top 3 vendors capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("brand engagement is highly concentrated in a few vendors.")
              : t("engagement is reasonably spread across vendors.")}
          </div>
        )
      }
      extraFilterFields={
        <>
        {(dealerMembershipType === "Vendor" || dealerMembershipType === "VendorDiamond" || dealerMembershipType === "VendorGemstone") && (
        <div className="col-md-3">
          <div className="input__block">
            <label>{t("Retailer(s)")}</label>
            <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
              {retailerList.map((item) => (
                <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
              ))}
            </Select>
          </div>
        </div>
        )}
        {(dealerMembershipType === "Retailer" || dealerMembershipType === "Combined") && (
        <div className="col-md-3">
          <div className="input__block">
            <label>{t("Vendor(s)")}</label>
            <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
              {vendorList.map((item) => (
                <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
              ))}
            </Select>
          </div>
        </div>
        )}
        </>
      }
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={retailerIds}
          dealerMembershipType={dealerMembershipType}
          filterParamName={VENDOR_CLICKS_CONFIG.detail.filterParamName}
          filterValue={filterValue}
          fileNamePart={VENDOR_CLICKS_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default JewelryVendorClickReportMain;
